// components/Sender/RunLogDialog.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SenderApi } from "./SenderApi";
import { MessageDetailDialog } from "./MessageDetailDialog";

const LEVEL_COLORS = {
  info: "#93c5fd",
  warn: "#fbbf24",
  error: "#f87171",
  debug: "#64748b",
};

const KIND_CFG = {
  chrome:  { icon: "🌐", label: "Chrome",  bg: "#dbeafe", fg: "#1d4ed8" },
  android: { icon: "📱", label: "Android", bg: "#cffafe", fg: "#0891b2" },
};

function _runtimeMeta(rid, runtimes) {
  const rt = (runtimes || []).find((r) => r.id === rid);
  const kind = rt?.kind || "unknown";
  return KIND_CFG[kind] || { icon: "❔", label: rid || "?",
                             bg: "#f3f4f6", fg: "#6b7280" };
}

export function RunLogDialog({ run, runtimes, onClose }) {
  const [logs, setLogs] = useState([]);
  const [messageInfo, setMessageInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [activeRuntime, setActiveRuntime] = useState("__all__");
  const boxRef = useRef(null);
  const tabsRef = useRef(null);

  // Автоскролл
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [logs]);

  // Поллинг
  useEffect(() => {
    if (!run?.id) return;
    let mounted = true;
    let cursor = 0;

    setLogs([]);
    setErr(null);

    const tick = async () => {
      try {
        const res = await SenderApi.getRunLogs(run.id, cursor);
        if (!mounted || !res) return;
        if (Array.isArray(res.logs) && res.logs.length) {
          setLogs((prev) => {
            const merged = prev.concat(res.logs);
            return merged.length > 5000 ? merged.slice(-5000) : merged;
          });
          cursor = res.last_id || cursor;
        }
      } catch (e) {
        if (mounted) setErr(e.message);
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => { mounted = false; clearInterval(id); };
  }, [run?.id, resetKey]);

  // Уникальные runtime_id + счётчики
  const { runtimeIds, counts } = useMemo(() => {
    const ids = new Set();
    const c = { __all__: logs.length };
    logs.forEach((l) => {
      if (l.runtime_id) {
        ids.add(l.runtime_id);
        c[l.runtime_id] = (c[l.runtime_id] || 0) + 1;
      }
    });
    return { runtimeIds: Array.from(ids).sort(), counts: c };
  }, [logs]);

  useEffect(() => {
    if (activeRuntime !== "__all__" && !runtimeIds.includes(activeRuntime)) {
      setActiveRuntime("__all__");
    }
  }, [runtimeIds, activeRuntime]);

  const filtered = useMemo(() => {
    if (activeRuntime === "__all__") return logs;
    return logs.filter((l) => l.runtime_id === activeRuntime);
  }, [logs, activeRuntime]);

  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return;
    const active = tabs.querySelector("[data-active='true']");
    if (active) {
      const rect = active.getBoundingClientRect();
      const parentRect = tabs.getBoundingClientRect();
      if (rect.right > parentRect.right || rect.left < parentRect.left) {
        active.scrollIntoView({ behavior: "smooth", inline: "center" });
      }
    }
  }, [activeRuntime, runtimeIds.length]);

  const clear = async () => {
    if (!run?.id) return;
    if (!window.confirm(
      `Удалить все логи run'а «${run.template_name || run.id}»?\n\n` +
      `Действие необратимо.`
    )) return;
    setClearing(true);
    try {
      await SenderApi.deleteRunLogs(run.id);
      setLogs([]);
      setResetKey((k) => k + 1);
    } catch (e) {
      setErr("Ошибка очистки: " + e.message);
    } finally {
      setClearing(false);
    }
  };

  if (!run) return null;

  const isActive = run.status === "running" || run.status === "stopping";

  return (
    <>
      <div style={overlay} onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} style={card}>
          <div style={headerBar}>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0 }}>
                📜 Журнал: {run.template_name || run.template_id || run.id}
              </h3>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                id: <code>{run.id}</code>
                {" · "}
                {isActive ? (
                  <span style={{ color: "#1d4ed8", fontWeight: 700 }}>
                    ▶ live · обновляется каждую секунду
                  </span>
                ) : (
                  <span style={{ color: "#6b7280" }}>
                    ⏸ архив · {logs.length} записей
                  </span>
                )}
                {runtimeIds.length > 0 && (
                  <span> · {runtimeIds.length} runtime'ов</span>
                )}
                {err && (
                  <span style={{ color: "#dc2626" }}> · ⚠️ {err}</span>
                )}
              </div>
            </div>
            <button className="btn-cancel"
                    style={{ fontSize: 12, padding: "6px 10px" }}
                    disabled={clearing}
                    onClick={clear}>
              {clearing ? "⏳ Очистка…" : "🧹 Очистить"}
            </button>
            <button className="btn-cancel" onClick={onClose}>✕</button>
          </div>

          {/* Вкладки runtime'ов */}
          {runtimeIds.length > 0 && (
            <div ref={tabsRef} style={tabsContainer}>
              <TabBtn
                label="Все"
                icon="📋"
                count={counts.__all__ || 0}
                active={activeRuntime === "__all__"}
                onClick={() => setActiveRuntime("__all__")}
                bg="#f3f4f6"
                fg="#374151"
              />
              {runtimeIds.map((rid) => {
                const meta = _runtimeMeta(rid, runtimes);
                return (
                  <TabBtn
                    key={rid}
                    label={rid}
                    icon={meta.icon}
                    count={counts[rid] || 0}
                    active={activeRuntime === rid}
                    onClick={() => setActiveRuntime(rid)}
                    bg={meta.bg}
                    fg={meta.fg}
                  />
                );
              })}
            </div>
          )}

          {/* Лог-бокс */}
          <div ref={boxRef} style={logBox}>
            {filtered.length === 0 ? (
              <div style={{ color: "#64748b" }}>
                {isActive ? "Ждём первые сообщения…" : "Записей нет."}
              </div>
            ) : (
              filtered.map((l, i) => {
                if (l.kind === "message" && l.payload) {
                  const info = l.payload;
                  const icon = info.test ? "🧪" : "📤";
                  const chShort = info.channel === "EMAIL" ? "EMAIL"
                    : info.channel === "CONTACT_FORM" ? "CF"
                    : info.channel === "CALENDLY" ? "CAL"
                    : info.channel;
                  return (
                    <div key={l.id || i} style={{ marginBottom: 2 }}>
                      <span style={{ color: "#475569", marginRight: 6 }}>
                        {fmtTime(l.ts)}
                      </span>
                      {icon}{" "}
                      {l.runtime_id && activeRuntime === "__all__" && (
                        <span style={inlineBadge(
                          _runtimeMeta(l.runtime_id, runtimes))}>
                          {_runtimeMeta(l.runtime_id, runtimes).icon}
                        </span>
                      )}
                      {info.block_tag && (
                        <span style={{ color: "#64748b" }}>
                          {info.block_tag}{" "}
                        </span>
                      )}
                      <b style={{ color: "#a5b4fc" }}>
                        #{info.intention_id}
                      </b>{" "}
                      <span style={{ color: "#94a3b8" }}>[{chShort}]</span>{" "}
                      <span style={{ color: "#e2e8f0" }}>{info.worker}</span>
                      {info.worker_sent != null && info.worker_limit != null && (
                        <span style={{ color: "#94a3b8" }}>
                          {" "}[{info.worker_sent}/
                          {info.worker_limit > 0 ? info.worker_limit : "∞"}]
                        </span>
                      )}
                      {" "}
                      <span style={{ color: "#9ca3af" }}>→</span>{" "}
                      <span style={{ color: "#cbd5e1" }}>{info.address}</span>
                      {"  "}
                      <span onClick={() => setMessageInfo(info)}
                            style={{ color: "#60a5fa", cursor: "pointer" }}>
                        детали
                      </span>
                    </div>
                  );
                }
                const color = LEVEL_COLORS[l.level] || "#cbd5e1";
                const icon =
                  l.level === "error" ? "✖ "
                  : l.level === "warn" ? "⚠ "
                  : l.level === "info" ? "· "
                  : "";
                return (
                  <div key={l.id || i} style={{ color, marginBottom: 2 }}>
                    <span style={{ color: "#475569", marginRight: 6 }}>
                      {fmtTime(l.ts)}
                    </span>
                    {l.runtime_id && activeRuntime === "__all__" && (
                      <span style={inlineBadge(
                        _runtimeMeta(l.runtime_id, runtimes))}>
                        {_runtimeMeta(l.runtime_id, runtimes).icon}
                      </span>
                    )}
                    {icon}{l.msg}
                  </div>
                );
              })
            )}
          </div>

          <div style={footer}>
            <span style={{ fontSize: 11, color: "#64748b" }}>
              Автоскролл только если ты внизу. Логи хранятся в локальной БД,
              доступны и после завершения.
            </span>
            <div style={{ flex: 1 }} />
            <button className="btn-primary" onClick={onClose}>Закрыть</button>
          </div>
        </div>
      </div>

      {messageInfo && (
        <MessageDetailDialog info={messageInfo}
          onClose={() => setMessageInfo(null)} />
      )}
    </>
  );
}

function TabBtn({ label, icon, count, active, onClick, bg, fg }) {
  return (
    <button
      data-active={active ? "true" : "false"}
      onClick={onClick}
      style={{
        flex: "0 0 auto",
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        borderRadius: 8,
        border: active ? `1.5px solid ${fg}` : "1px solid #e5e7eb",
        background: active ? bg : "#fff",
        color: active ? fg : "#6b7280",
        fontWeight: active ? 700 : 500,
        fontSize: 12,
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontFamily: "inherit",
      }}>
      <span>{icon}</span>
      <span style={{ maxWidth: 180, overflow: "hidden",
                     textOverflow: "ellipsis" }}>
        {label}
      </span>
      <span style={{
        background: active ? fg : "#f3f4f6",
        color: active ? "#fff" : "#6b7280",
        fontSize: 10, fontWeight: 700,
        padding: "1px 6px", borderRadius: 10,
      }}>
        {count}
      </span>
    </button>
  );
}

function inlineBadge(meta) {
  return {
    display: "inline-block",
    padding: "0 5px",
    marginRight: 4,
    borderRadius: 4,
    background: meta.bg,
    color: meta.fg,
    fontSize: 10,
    fontWeight: 700,
    verticalAlign: "baseline",
  };
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch { return ""; }
}

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,.7)",
  backdropFilter: "blur(4px)", zIndex: 1400,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};
const card = {
  background: "#fff", borderRadius: 14, width: "100%",
  maxWidth: 1100, maxHeight: "92vh", display: "flex",
  flexDirection: "column", overflow: "hidden",
};
const headerBar = {
  padding: "16px 20px", borderBottom: "1.5px solid #e5e7eb",
  display: "flex", gap: 10, alignItems: "flex-start",
};
const logBox = {
  flex: 1, overflow: "auto", margin: 16, padding: 12,
  background: "#0b1220", borderRadius: 10,
  fontFamily: "Consolas, monospace", fontSize: 13,
  lineHeight: 1.5, color: "#cbd5e1",
  border: "1px solid #1e293b",
};
const footer = {
  padding: "12px 20px", borderTop: "1.5px solid #e5e7eb",
  display: "flex", gap: 8, alignItems: "center",
};
const tabsContainer = {
  display: "flex",
  gap: 6,
  overflowX: "auto",
  overflowY: "hidden",
  padding: "10px 16px 6px",
  scrollbarWidth: "thin",
  borderBottom: "1px solid #e5e7eb",
};